// src/Services/paystackService.js
import PaystackPop from '@paystack/inline-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import authService from './authService';
import { PRODUCTS } from './products';
import analytics from './analyticsService';

const PENDING_PAYMENTS_KEY = 'nameit_pending_payments';
const paystackPublicKey = (import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || "pk_live_2c865099dea44904afdbba5d00734785e97f8982").trim();

class PaystackService {
  constructor() {
    // Use test key for now - replace with live key in production
    this.publicKey = paystackPublicKey 
    this.baseUrl = "https://api.paystack.co";
  }

  // Generate payment reference
  generatePaymentReference() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `nameit_${timestamp}_${random}`;
  }

  // Get user payment details
  async getUserPaymentDetails() {
    try {
      const userId = await authService.getCurrentUserId();
      const playerName = localStorage.getItem("playerName") || "Player";
      
      return {
        email: `${userId}@nameit.temp`, // Temporary email for anonymous users
        name: playerName,
        phone: "", // Optional - can collect later
      };
    } catch (error) {
      console.error("Error getting user details:", error);
      return {
        email: `anonymous_${Date.now()}@nameit.temp`,
        name: "Anonymous Player",
        phone: ""
      };
    }
  }

  /**
   * Opens Paystack for a product from Services/products.js and, once paid,
   * asks the server to verify it with Paystack and grant it. Resolves with the
   * server's grant; rejects if the player cancels or verification fails.
   * mobileRef: set when this checkout was opened from the mobile app's deep
   * link, so the payment can be matched back to mobile's pending purchase.
   */
  async payForProduct({ productId, email, name, roomId = null, mobileRef = null, label = "NameIt!" }) {
    const product = PRODUCTS[productId];
    if (!product) throw new Error(`Unknown product: ${productId}`);
    const userId = await authService.getCurrentUserId();
    const reference = this.generatePaymentReference();

    const paid = await new Promise((resolve, reject) => {
      new PaystackPop().newTransaction({
        key: this.publicKey,
        email,
        amount: product.priceNgn * 100,
        reference,
        currency: "NGN",
        channels: ["card", "bank", "ussd", "bank_transfer", "mobile_money"],
        label,
        metadata: {
          userId,
          product: productId,
          ...(roomId && { roomId }),
          ...(mobileRef && { mobileRef }),
          playerName: name,
          custom_fields: [
            { display_name: "Product", variable_name: "product", value: productId },
            { display_name: "Player Name", variable_name: "player_name", value: name },
          ],
        },
        onSuccess: (transaction) => resolve(transaction),
        onCancel: () => reject(Object.assign(new Error("Payment cancelled"), { cancelled: true })),
      });
    });

    this.rememberPending(paid.reference);
    const grant = await this.confirmPayment(paid.reference);
    this.forgetPending(paid.reference);
    return grant;
  }

  // Paid-but-unconfirmed references survive reloads so a flaky network or a
  // server hiccup never loses a purchase: retryPendingPayments runs on app load.
  pendingReferences() {
    try {
      return JSON.parse(localStorage.getItem(PENDING_PAYMENTS_KEY) || "[]");
    } catch {
      return [];
    }
  }

  rememberPending(reference) {
    const refs = this.pendingReferences();
    if (!refs.includes(reference)) {
      localStorage.setItem(PENDING_PAYMENTS_KEY, JSON.stringify([...refs, reference]));
    }
  }

  forgetPending(reference) {
    localStorage.setItem(
      PENDING_PAYMENTS_KEY,
      JSON.stringify(this.pendingReferences().filter((r) => r !== reference))
    );
  }

  // Returns how many payments were granted. Unauthenticated/network errors
  // keep the reference for next time; permanent rejections drop it.
  async retryPendingPayments() {
    const params = new URLSearchParams(window.location.search);
    const fromLink = params.get("confirmPayment");
    if (fromLink) {
      this.rememberPending(fromLink);
      params.delete("confirmPayment");
      const query = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (query ? `?${query}` : ""));
    }

    const confirm = httpsCallable(getFunctions(), "confirmPayment");
    let granted = 0;
    for (const reference of this.pendingReferences()) {
      try {
        const { data } = await confirm({ reference });
        this.forgetPending(reference);
        if (!data.alreadyProcessed) granted++;
      } catch (error) {
        const code = error?.code?.replace("functions/", "");
        if (["permission-denied", "invalid-argument"].includes(code)) this.forgetPending(reference);
      }
    }
    return granted;
  }

  // The server verifies with Paystack and grants at most once per reference,
  // so retrying after a network error is safe.
  async confirmPayment(reference) {
    const confirm = httpsCallable(getFunctions(), "confirmPayment");
    let lastError;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await confirm({ reference });
        return { reference, ...data };
      } catch (error) {
        lastError = error;
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    analytics.track("payment_confirm_failed", {
      reason: lastError?.code || lastError?.message || "unknown",
      attempts: 3,
    });
    throw Object.assign(new Error(lastError?.message || "Could not confirm payment"), { reference });
  }

  // Format amount for display
  formatAmount(amount) {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  }

  // Get available payment methods in Nigeria
  getPaymentMethods() {
    return [
      {
        name: "Debit Card",
        description: "Visa, Mastercard, Verve",
        icon: "💳",
        popular: true
      },
      {
        name: "Bank Transfer", 
        description: "Direct bank transfer",
        icon: "🏦",
        popular: true
      },
      {
        name: "USSD",
        description: "Pay with phone (*737#)",
        icon: "📱",
        popular: true
      },
      {
        name: "Mobile Money",
        description: "MTN, Airtel, etc.",
        icon: "📲",
        popular: false
      }
    ];
  }
}

export const paystackService = new PaystackService();
export default paystackService;
